import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Building2, Info, Link2 } from "lucide-react";
import { getActiveClientsApi } from "../../../../api/company";
import useAuth from "../../../../hooks/useAuth";
import { roleRoutes } from "../../../../utils/roles";
import { showToast } from "../../../../utils/showToast";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Modal } from "../../../../components/ui/Modal";
import { SearchableSelect } from "../../../../components/ui/SearchableSelect";
import { commerceSecondaryLine, matchCommerce, toCommerceOption } from "../../../../utils/commerceSearch";
import { formatCommerceOptionLabel } from "../../cobro/components/commerceOptionLabel";
import { MEMBERSHIP_SOURCE_LABELS } from "../../cobro/utils/holdings";
import { linkCompanyToHolding, linkErrorText } from "./linkCompanyToHolding";
import CreateHoldingCompanyModal from "./CreateHoldingCompanyModal";

// Diferencias de membresía que NO bloquean el cobro (cuenta la unión de las dos fuentes) y que
// corrige el script de reconciliación de contracts. Rótulo (clave i18n) por tipo.
const INCONSISTENCY_LABELS = {
  missingInList: "{{n}} empresa(s) apuntan al holding pero no están en su lista.",
  missingHoldingId: "{{n}} empresa(s) están en la lista del holding pero su ficha no apunta a él.",
  danglingHoldingId: "{{n}} empresa(s) de la lista apuntan a un holding que no existe.",
  duplicateIds: "{{n}} empresa(s) aparecen repetidas en la lista.",
  orphanIds: "{{n}} entrada(s) de la lista son de empresas que no existen.",
};

const SOURCE_TONES = { both: "success", list: "warning", holdingId: "warning" };

// Roles que pueden vincular: el admin y el superadmin logístico del operador, la misma
// allowlist por `role._id` que OperatorAdminGuard de contracts. Soporte y supervisor ven la
// ficha pero recibirían 403 `forbidden_role`. La autoridad es contracts; esto evita ofrecer una
// acción que va a fallar.
const LINK_ROLE_IDS = new Set([roleRoutes.admin, roleRoutes.superadmin]);

// react-select entrega `{ label, value, data }`: los campos del comercio quedan en `data`.
const filterCommerceOption = (option, query) => matchCommerce(option.data, query);

/**
 * Empresas del holding según contracts (regla de la unión de `holding.companies[]` y
 * `company.holdingId`), con de dónde sale cada una, las inconsistencias de membresía y las
 * acciones de crear una empresa nueva en el holding (con su contrato replicado) y de vincular
 * una empresa existente del operador.
 *
 * Solo los CONFLICTOS (una empresa en más de un holding) bloquean la nota consolidada; el
 * resto se cobra igual y lo repara el script de reconciliación, que corre soporte.
 *
 * Vincular no se puede deshacer desde acá y da al portal del holding acceso a los envíos de esa
 * empresa: pide confirmación nombrando la empresa (nombre y RUT) y sus consecuencias. Solo lo
 * pueden hacer el admin y el superadmin logístico; para el resto la acción queda deshabilitada
 * y se explica por qué.
 *
 * @param {{ holding: object, holdingNameOf: (id: string) => string|null,
 *           consolidatedEnabled?: boolean, onLinked: () => void }} props
 *   `holding` es un elemento de GET contracts/holdings; `consolidatedEnabled`, el estado de su
 *   cobro consolidado (undefined = desconocido); `onLinked` recarga los holdings.
 */
export default function HoldingCompaniesSection({ holding, holdingNameOf, consolidatedEnabled, onLinked }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;
  // `user.role` es el _id del rol del token (AuthProvider), como en el resto de los permisos.
  const canLink = LINK_ROLE_IDS.has(user?.role);
  const linkRestrictedHintId = useId();
  const linkRestrictedText = t("Solo un administrador o un superadministrador logístico puede vincular empresas a un holding.");

  const [clients, setClients] = useState([]);
  const [clientsError, setClientsError] = useState(false);
  const [toLink, setToLink] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Comercios del operador con contrato activo: son los que contracts deja vincular (404
  // company_not_found si la empresa no tiene ningún contrato con el operador).
  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let alive = true;
    Promise.resolve(getActiveClientsApi(logisticCompanyId))
      .then((data) => {
        if (!alive) return;
        setClients(Array.isArray(data) ? data.map((c) => toCommerceOption(c, t("Sin nombre"))) : []);
        setClientsError(!Array.isArray(data));
      })
      .catch(() => {
        if (alive) setClientsError(true);
      });
    return () => {
      alive = false;
    };
  }, [logisticCompanyId, t]);

  const companies = useMemo(
    () => (Array.isArray(holding?.companies) ? holding.companies : []),
    [holding],
  );
  const inconsistencies = holding?.inconsistencies ?? {};
  const conflicts = useMemo(
    () => (Array.isArray(holding?.inconsistencies?.conflicts) ? holding.inconsistencies.conflicts : []),
    [holding],
  );
  const conflictByCompany = useMemo(
    () => new Map(conflicts.map((c) => [String(c.companyId), c])),
    [conflicts],
  );
  const memberIds = useMemo(() => new Set(companies.map((c) => String(c.id))), [companies]);
  const linkOptions = useMemo(
    () => clients.filter((c) => !memberIds.has(String(c.value))),
    [clients, memberIds],
  );
  const repairable = Object.keys(INCONSISTENCY_LABELS)
    .map((key) => ({ key, n: Array.isArray(inconsistencies[key]) ? inconsistencies[key].length : 0 }))
    .filter((item) => item.n > 0);
  const invalidEntries = Number(inconsistencies.invalidEntries) || 0;

  const conflictText = (conflict) => {
    const names = (conflict.holdingIds ?? [])
      .filter((id) => String(id) !== String(holding.id))
      .map((id) => holdingNameOf(id) ?? id);
    const parts = [];
    if (names.length) parts.push(t("También está en: {{names}}.", { names: names.join(", ") }));
    if (conflict.otherOperatorHoldings > 0) {
      parts.push(t("Y en {{n}} holding(s) de otro operador.", { n: conflict.otherOperatorHoldings }));
    }
    return parts.join(" ");
  };

  // La empresa elegida, para nombrarla en la confirmación.
  const toLinkOption = linkOptions.find((option) => String(option.value) === String(toLink)) ?? null;
  const holdingName = holding.name || t("Sin nombre");

  // Qué pasa con su cobro, según el interruptor del holding.
  const billingConsequence =
    consolidatedEnabled === true
      ? t("Se va a cobrar dentro de la nota consolidada del holding, que tiene el cobro consolidado encendido, y deja de cobrarse sola.")
      : consolidatedEnabled === false
        ? t("Mientras el cobro consolidado del holding esté apagado se sigue cobrando sola; si se enciende, pasa a cobrarse dentro de la nota del holding.")
        : t("Si el cobro consolidado del holding está encendido, se va a cobrar dentro de su nota y deja de cobrarse sola.");

  const handleLink = async () => {
    if (!toLink || !canLink) return;
    setLinking(true);
    setLinkError(null);
    const outcome = await linkCompanyToHolding(holding.id, toLink);
    setLinking(false);
    setConfirmOpen(false);
    if (outcome.ok) {
      showToast(t("Empresa vinculada al holding."), "success");
      setToLink("");
      onLinked?.();
      return;
    }
    const text = linkErrorText(outcome.error, t, holdingNameOf);
    setLinkError(text);
    showToast(text, "error");
  };

  const handleCreated = (result) => {
    setCreateOpen(false);
    showToast(
      result?.contractId
        ? t("Empresa creada con el contrato del holding (RUT {{rut}}).", { rut: result.businessIdentifier })
        : t("Empresa creada (RUT {{rut}}).", { rut: result?.businessIdentifier ?? "—" }),
      "success",
    );
    onLinked?.();
  };

  return (
    <div className="space-y-4">
      {conflicts.length > 0 && (
        <div className="rounded-md bg-status-error-soft p-3 text-sm text-status-error" role="alert">
          <p className="flex items-start gap-2 font-medium">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {t("{{n}} empresa(s) están en más de un holding: la nota de este holding queda bloqueada hasta resolverlo.", {
              n: conflicts.length,
            })}
          </p>
          <p className="mt-1 text-xs">
            {t("Desde acá no se puede desvincular una empresa: pedile a soporte de Beartrack que la deje en un solo holding.")}
          </p>
        </div>
      )}

      {(repairable.length > 0 || invalidEntries > 0) && (
        <div className="rounded-md border border-border bg-bg-subtle p-3 text-xs text-fg-muted">
          <p className="flex items-start gap-2 font-medium text-fg">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {t("La lista del holding y la ficha de sus empresas no coinciden del todo.")}
          </p>
          <ul className="ml-6 mt-1 list-disc space-y-0.5">
            {repairable.map(({ key, n }) => (
              <li key={key}>{t(INCONSISTENCY_LABELS[key], { n })}</li>
            ))}
            {invalidEntries > 0 && (
              <li>{t("{{n}} entrada(s) inválidas en la lista.", { n: invalidEntries })}</li>
            )}
          </ul>
          <p className="mt-1">
            {t("No bloquean el cobro: cuenta la unión de las dos fuentes. Soporte las corrige con el script de reconciliación.")}
          </p>
        </div>
      )}

      {companies.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("El holding no tiene empresas.")}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t("Empresas del holding")}>
          {companies.map((c) => {
            const conflict = conflictByCompany.get(String(c.id));
            const secondary = commerceSecondaryLine(c);
            return (
              <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-fg">
                    {c.name || t("Sin nombre")}
                  </span>
                  {secondary && <span className="block truncate text-xs text-fg-muted">{secondary}</span>}
                  {conflict && (
                    <span className="block text-xs text-status-error">{conflictText(conflict)}</span>
                  )}
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {conflict && <Badge tone="error">{t("En más de un holding")}</Badge>}
                  <Badge tone={SOURCE_TONES[c.source] ?? "neutral"}>
                    {t(MEMBERSHIP_SOURCE_LABELS[c.source] ?? c.source)}
                  </Badge>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <section className="space-y-2 rounded-lg border border-border p-3" aria-label={t("Crear una empresa nueva")}>
        <h4 className="text-sm font-semibold text-fg">{t("Crear una empresa nueva")}</h4>
        <p className="text-xs text-fg-muted">
          {t("Se crea con el contrato del holding (el más reciente de sus empresas, con inicio hoy), con o sin integración, y queda vinculada: su cobro sigue la misma regla que al vincular.")}
        </p>
        <Button
          variant="outline"
          onClick={() => setCreateOpen(true)}
          disabled={!canLink}
          title={canLink ? undefined : t("Solo un administrador o un superadministrador logístico puede crear empresas en un holding.")}
          iconLeft={<Building2 className="h-4 w-4" />}
        >
          {t("Crear empresa")}
        </Button>
        {!canLink && (
          <p className="text-xs text-fg-muted">
            {t("Solo un administrador o un superadministrador logístico puede crear empresas en un holding.")}
          </p>
        )}
      </section>

      <section className="space-y-2 rounded-lg border border-border p-3" aria-label={t("Vincular una empresa")}>
        <h4 className="text-sm font-semibold text-fg">{t("Vincular una empresa")}</h4>
        <p className="text-xs text-fg-muted">{billingConsequence}</p>
        {clientsError && (
          <p className="text-xs text-status-warning">
            {t("No se pudieron cargar los comercios del operador.")}
          </p>
        )}
        {/* Visible además del title: un botón deshabilitado no muestra su tooltip en todos los
            navegadores ni con teclado. */}
        {!canLink && (
          <p id={linkRestrictedHintId} className="text-xs text-fg-muted">
            {linkRestrictedText}
          </p>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <SearchableSelect
              options={linkOptions}
              value={toLink}
              onChange={(v) => {
                setToLink(v ?? "");
                setLinkError(null);
              }}
              placeholder={t("Buscar comercio…")}
              isClearable
              isDisabled={!canLink}
              noOptionsText={t("Sin comercios")}
              filterOption={filterCommerceOption}
              formatOptionLabel={formatCommerceOptionLabel}
              aria-label={t("Empresa a vincular")}
            />
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!canLink || !toLink || linking}
            title={canLink ? undefined : linkRestrictedText}
            aria-describedby={canLink ? undefined : linkRestrictedHintId}
            iconLeft={<Link2 className="h-4 w-4" />}
          >
            {t("Vincular")}
          </Button>
        </div>
        {linkError && (
          <p className="text-sm text-status-error" role="alert">
            {linkError}
          </p>
        )}
      </section>

      <CreateHoldingCompanyModal
        open={createOpen}
        holding={holding}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!linking) setConfirmOpen(false);
        }}
        size="md"
        title={t("¿Vincular esta empresa al holding {{holding}}?", { holding: holdingName })}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={linking}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleLink} loading={linking} iconLeft={<Link2 className="h-4 w-4" />}>
              {t("Sí, vincular")}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-fg">
          <div className="rounded-md border border-border bg-bg-subtle px-3 py-2">
            <p className="font-medium">{toLinkOption?.label || t("Sin nombre")}</p>
            <p className="text-xs text-fg-muted">
              {toLinkOption?.businessIdentifier
                ? t("RUT {{rut}}", { rut: toLinkOption.businessIdentifier })
                : t("Sin RUT")}
            </p>
          </div>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              {t("Los usuarios del portal del holding van a ver sus envíos, con los datos de sus destinatarios, y van a poder editar su ficha.")}
            </li>
            <li>{billingConsequence}</li>
            <li>{t("Desde acá no se puede deshacer: solo soporte de Beartrack puede desvincularla.")}</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}
